import { useMemo } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { Icon, Modal, Portal, Text, useTheme } from 'react-native-paper'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { useReverseGeocode } from '@/hooks/useReverseGeocode'
import { formatDateWithWeekday, formatTimeOfDay } from '@/lib/dates'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { Button } from '@/ui/Button'
import MapView, { Marker } from '@/ui/MapView'

interface ManualEntryInfoDialogProps {
    visible: boolean
    entry: TimeEntriesExpanded | null
    onDismiss: () => void
}

interface LocationPoint {
    lat: number
    lng: number
}

function parseLocation(value: unknown): LocationPoint | null {
    if (!value || typeof value !== 'object') return null
    const loc = value as Record<string, unknown>
    if (typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return null
    return { lat: loc.lat, lng: loc.lng }
}

function formatCoords(point: LocationPoint | null): string {
    if (!point) return 'No location recorded'
    return `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
}

function useMapRegion(entry: TimeEntriesExpanded | null) {
    return useMemo(() => {
        if (!entry) return { clockIn: null, clockOut: null, region: null }

        const clockIn = parseLocation(entry.clock_in_location)
        const clockOut = parseLocation(entry.clock_out_location)
        const points = [clockIn, clockOut].filter(Boolean) as LocationPoint[]

        if (points.length === 0) return { clockIn: null, clockOut: null, region: null }

        const lats = points.map(p => p.lat)
        const lngs = points.map(p => p.lng)
        const region = {
            latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
            longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
            latitudeDelta: Math.max((Math.max(...lats) - Math.min(...lats)) * 1.5, 0.002),
            longitudeDelta: Math.max((Math.max(...lngs) - Math.min(...lngs)) * 1.5, 0.002),
        }

        return { clockIn, clockOut, region }
    }, [entry])
}

export function ManualEntryInfoDialog({ visible, entry, onDismiss }: ManualEntryInfoDialogProps) {
    const theme = useTheme()
    useDismissOnEscape(visible, onDismiss)
    const { clockIn, clockOut, region } = useMapRegion(entry)

    const clockInGeo = useReverseGeocode({
        latitude: clockIn?.lat,
        longitude: clockIn?.lng,
        enabled: visible && !!clockIn,
    })
    const clockOutGeo = useReverseGeocode({
        latitude: clockOut?.lat,
        longitude: clockOut?.lng,
        enabled: visible && !!clockOut,
    })

    if (!visible || !entry) return null

    const createdAt = entry.created
        ? `${formatDateWithWeekday(entry.created)} at ${formatTimeOfDay(entry.created)}`
        : 'Unknown'

    return (
        <Portal>
            <Modal
                visible
                onDismiss={onDismiss}
                contentContainerStyle={[
                    styles.container,
                    { backgroundColor: theme.colors.surface },
                ]}
            >
                <View style={styles.header}>
                    <Text variant="titleMedium">Entry Info</Text>
                    <Button title="✕" onPress={onDismiss} mode="text" />
                </View>
                <View style={styles.content}>
                    <View style={styles.createdRow}>
                        <Text
                            variant="labelMedium"
                            style={{ color: theme.colors.onSurfaceVariant }}
                        >
                            Created:
                        </Text>
                        <Text variant="bodySmall">{createdAt}</Text>
                    </View>
                    <View style={styles.locationsRow}>
                        <LocationRow label="Clock in" point={clockIn} geocode={clockInGeo} />
                        <LocationRow label="Clock out" point={clockOut} geocode={clockOutGeo} />
                    </View>
                </View>
                <LocationMiniMap clockIn={clockIn} clockOut={clockOut} region={region} />
            </Modal>
        </Portal>
    )
}

function LocationRow({
    label,
    point,
    geocode,
}: {
    label: string
    point: LocationPoint | null
    geocode: ReturnType<typeof useReverseGeocode>
}) {
    const theme = useTheme()

    let displayText = 'No location recorded'
    if (point) {
        if (geocode.data) {
            displayText = geocode.data.placeName || geocode.data.address
        } else if (geocode.isLoading) {
            displayText = ''
        } else {
            displayText = formatCoords(point)
        }
    }

    return (
        <View style={styles.locationCol}>
            <Text variant="labelSmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {label}
            </Text>
            {point && geocode.isLoading ? (
                <ActivityIndicator size={12} />
            ) : (
                <Text variant="bodySmall" numberOfLines={2}>
                    {displayText}
                </Text>
            )}
        </View>
    )
}

function LocationMiniMap({
    clockIn,
    clockOut,
    region,
}: {
    clockIn: LocationPoint | null
    clockOut: LocationPoint | null
    region: {
        latitude: number
        longitude: number
        latitudeDelta: number
        longitudeDelta: number
    } | null
}) {
    const theme = useTheme()

    if (!region) {
        return (
            <View style={[styles.emptyMap, { backgroundColor: theme.colors.surfaceVariant }]}>
                <Icon source="map-marker-off" size={32} color={theme.colors.onSurfaceVariant} />
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                    No location data
                </Text>
            </View>
        )
    }

    const initialCamera = {
        center: { latitude: region.latitude, longitude: region.longitude },
        zoom: 15,
        heading: 0,
        pitch: 0,
    }

    return (
        <View style={styles.mapWrapper}>
            <MapView
                style={styles.map}
                initialRegion={region}
                initialCamera={initialCamera}
                showsUserLocation={false}
                showsMyLocationButton={false}
                showsCompass={false}
                showsTraffic={false}
                showsBuildings={false}
                showsIndoors={false}
                toolbarEnabled={false}
                scrollEnabled={false}
                zoomEnabled={false}
                rotateEnabled={false}
                pitchEnabled={false}
            >
                {clockIn && (
                    <Marker
                        coordinate={{ latitude: clockIn.lat, longitude: clockIn.lng }}
                        title="Clock In"
                        pinColor="green"
                    />
                )}
                {clockOut && (
                    <Marker
                        coordinate={{ latitude: clockOut.lat, longitude: clockOut.lng }}
                        title="Clock Out"
                        pinColor="red"
                    />
                )}
            </MapView>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        margin: 20,
        borderRadius: 12,
        alignSelf: 'center',
        width: 340,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8,
    },
    content: {
        paddingHorizontal: 20,
        paddingBottom: 12,
        gap: 8,
    },
    createdRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    locationsRow: {
        flexDirection: 'row',
        gap: 16,
    },
    locationCol: {
        flex: 1,
        gap: 2,
    },
    mapWrapper: {
        height: 180,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: '#e0e0e0',
    },
    map: {
        flex: 1,
    },
    emptyMap: {
        height: 120,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
    },
})
